const _isoDatePattern =
  /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/

export const tryParseJson = <T>(json?: string, defaultValue?: T): T | undefined => {
  if (!json) {
    return defaultValue
  }
  try {
    return JSON.parse(json) as never
  } catch (_e) {
    return defaultValue
  }
}

export const parseNonObjectJson = <T = any>(json: any): T => {
  return typeof json !== 'object' ? JSON.parse(json) : json
}

export const tryParseSelectJson = <T>(json: T, cb: (value: T) => unknown) => {
  const parsed = typeof json === 'string' ? tryParseJson<T>(json) : json
  return parsed ? cb(parsed) : undefined
}

export const tryParseLazySelectJson =
  <T, V>(cb: (value: T) => V) =>
  (json: T) => {
    return tryParseSelectJson<T>(json, cb)
  }

export interface DataResponse<T> extends Response {
  data: T
}

export const normalizeUrl = (url?: string) => {
  if (!url) {
    return url
  }
  return url.replace(/\/$/, '')
}

const objectToString = Object.prototype.toString

const isError = (value: Error) => objectToString.call(value) === '[object Error]'

const networkErrorMessages = new Set([
  'network error', // Chrome
  'Failed to fetch', // Chrome
  'NetworkError when attempting to fetch resource.', // Firefox
  'The Internet connection appears to be offline.', // Safari 16
  'Load failed', // Safari 17+
  'Network request failed', // `cross-fetch`
  'fetch failed', // Undici (Node.js)
])

export default function isNetworkError(error: Error) {
  // https://github.com/sindresorhus/is-network-error/blob/main/index.js
  // noinspection SuspiciousTypeOfGuard
  const isValid = error && isError(error) && error.name === 'TypeError' && typeof error.message === 'string'

  if (!isValid) {
    return false
  }

  // We do an extra check for Safari 17+ as it has a very generic error message.
  // Network errors in Safari have no stack.
  if (error.message === 'Load failed') {
    return error.stack === undefined
  }

  return networkErrorMessages.has(error.message)
}

/**
 * Extracts the text output from an OpenAI SDK response object (Responses API), if available.
 * The function is intentionally untyped to avoid a hard dependency on the OpenAI SDK types.
 */
export function getOpenAIOutputText(response: any): string {
  try {
    const out = response?.output_text
    return typeof out === 'string' ? out : (out ?? '').toString()
  } catch {
    return ''
  }
}

/**
 * Parses a JSON string into the requested generic type T.
 * Returns undefined if parsing fails, or the provided fallback value.
 */
export function parseJsonAs<T>(text: string, fallback?: T): T | undefined {
  try {
    return JSON.parse(text) as T
  } catch {
    return fallback
  }
}

/**
 * Convenience helper to parse OpenAI textual output into a JSON value of type T.
 * - Extracts output_text and trims it
 * - JSON.parse into T
 * - Optionally normalize arrays of primitives
 */
export function parseAndNormalizeOpenAIResponse<T>(
  response: any,
  opts?: { fallback?: T; skipNormalizeArrayOfPrimitives?: boolean },
): T | undefined {
  const raw = getOpenAIOutputText(response).trim()
  const parsed = parseJsonAs<T>(raw, opts?.fallback)
  if (!parsed) return parsed
  if (!opts?.skipNormalizeArrayOfPrimitives && Array.isArray(parsed)) {
    return parsed.map((v: any) => (v == null ? '' : String(v)).trim()).filter(Boolean) as T
  }
  return parsed
}
